import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, Filter, Package, Truck, Store, Utensils, Clock,
  CheckCircle2, XCircle, Phone, MapPin, Euro, RefreshCw,
  ChevronRight, User, Globe, Bike, Car, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { BackButton } from '@/components/ui/back-button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Source icons and colors
const SOURCE_CONFIG = {
  lieferando: { icon: Bike, color: 'bg-orange-500', text: 'Lieferando' },
  wolt: { icon: Bike, color: 'bg-cyan-500', text: 'Wolt' },
  ubereats: { icon: Car, color: 'bg-green-600', text: 'Uber Eats' },
  website: { icon: Globe, color: 'bg-purple-500', text: 'Website' },
  phone: { icon: Phone, color: 'bg-blue-500', text: 'Telefon' },
  pos: { icon: Store, color: 'bg-amber-500', text: 'Kasse' },
  default: { icon: Package, color: 'bg-gray-500', text: 'Sonstige' }
};

export default function OrdersView({ sessionToken, onBack }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cashx/kasse/active-orders`, {
        headers: { 'X-Session-Token': sessionToken }
      });
      
      if (response.ok) {
        const data = await response.json();
        const allOrders = [
          ...(data.new || []).map(o => ({ ...o, status: 'new' })),
          ...(data.processing || []).map(o => ({ ...o, status: 'processing' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setOrders(allOrders);
      }
    } catch (error) {
      console.error('Fetch orders error:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Filter orders
  const filteredOrders = orders.filter(order => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesNumber = order.order_number?.toString().includes(query);
      const matchesCustomer = order.customer?.name?.toLowerCase().includes(query);
      const matchesPhone = order.customer?.phone?.includes(query);
      if (!matchesNumber && !matchesCustomer && !matchesPhone) return false;
    }
    
    // Tab filter
    if (activeTab === 'delivery') return order.delivery_type === 'delivery';
    if (activeTab === 'pickup') return order.delivery_type === 'pickup';
    if (activeTab === 'dine_in') return order.delivery_type === 'dine_in';
    if (activeTab === 'new') return order.status === 'new';
    
    return true;
  });

  // Get source config
  const getSourceConfig = (source) => {
    return SOURCE_CONFIG[source?.toLowerCase()] || SOURCE_CONFIG.default;
  };

  // Get time since
  const getTimeSince = (timestamp) => {
    if (!timestamp) return '';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000 / 60);
    if (diff < 1) return 'Gerade eben';
    if (diff < 60) return `${diff} Min.`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/cashx/kasse/order/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`Status auf "${newStatus}" geändert`);
        fetchOrders();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <BackButton 
              onClick={onBack} 
              label="Zurück" 
              className="text-gray-400 hover:text-white"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-white">Bestellungen</h1>
            <p className="text-gray-400">{orders.length} aktive Bestellungen</p>
          </div>
        </div>
        <Button onClick={fetchOrders} variant="outline" className="border-gray-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Bestellung suchen (#, Name, Telefon)..."
            className="pl-10 bg-[#12121a] border-gray-700 text-white"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="bg-[#12121a] border border-gray-800 p-1 w-fit">
          <TabsTrigger value="all" className="data-[state=active]:bg-gray-700">
            Alle ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="new" className="data-[state=active]:bg-amber-600">
            Neu ({orders.filter(o => o.status === 'new').length})
          </TabsTrigger>
          <TabsTrigger value="delivery" className="data-[state=active]:bg-purple-600">
            <Truck className="w-4 h-4 mr-1" />
            Lieferung
          </TabsTrigger>
          <TabsTrigger value="pickup" className="data-[state=active]:bg-amber-600">
            <Package className="w-4 h-4 mr-1" />
            Abholung
          </TabsTrigger>
          <TabsTrigger value="dine_in" className="data-[state=active]:bg-blue-600">
            <Utensils className="w-4 h-4 mr-1" />
            Im Haus
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="flex-1 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Orders List */}
            <div className="lg:col-span-2">
              <Card className="bg-[#12121a] border-gray-800 h-full">
                <CardContent className="p-0">
                  <ScrollArea className="h-[calc(100vh-300px)]">
                    {loading ? (
                      <div className="p-8 text-center text-gray-400">Lade...</div>
                    ) : filteredOrders.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        Keine Bestellungen gefunden
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-800">
                        {filteredOrders.map((order) => {
                          const sourceConfig = getSourceConfig(order.source);
                          const SourceIcon = sourceConfig.icon;
                          const isSelected = selectedOrder?.order_id === order.order_id;
                          
                          return (
                            <div
                              key={order.order_id}
                              onClick={() => setSelectedOrder(order)}
                              className={`
                                p-4 cursor-pointer transition-colors
                                ${isSelected ? 'bg-gray-800' : 'hover:bg-gray-800/50'}
                              `}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  {/* Source Badge */}
                                  <div className={`w-10 h-10 rounded-lg ${sourceConfig.color} flex items-center justify-center`}>
                                    <SourceIcon className="w-5 h-5 text-white" />
                                  </div>
                                  
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-bold text-white">
                                        #{order.order_number}
                                      </span>
                                      <Badge 
                                        variant="outline"
                                        className={`text-xs ${
                                          order.status === 'new' 
                                            ? 'text-amber-400 border-amber-500/30' 
                                            : 'text-blue-400 border-blue-500/30'
                                        }`}
                                      >
                                        {order.status === 'new' ? 'Neu' : 'In Arbeit'}
                                      </Badge>
                                    </div>
                                    <div className="text-sm text-gray-400 flex items-center gap-2">
                                      <Clock className="w-3 h-3" />
                                      {getTimeSince(order.created_at)}
                                      <span>•</span>
                                      <span>{sourceConfig.text}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="text-right flex items-center gap-3">
                                  <div>
                                    <div className="font-bold text-white">
                                      {(order.total || 0).toFixed(2)} €
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {order.items?.length || 0} Artikel
                                    </div>
                                  </div>
                                  <ChevronRight className="w-5 h-5 text-gray-600" />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Order Details */}
            <div>
              <Card className="bg-[#12121a] border-gray-800 h-full">
                <CardHeader className="border-b border-gray-800">
                  <CardTitle className="text-lg text-white">
                    {selectedOrder ? `Bestellung #${selectedOrder.order_number}` : 'Details'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {!selectedOrder ? (
                    <div className="text-center py-8 text-gray-500">
                      Bestellung auswählen
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Customer Info */}
                      {selectedOrder.customer && (
                        <div className="p-3 bg-gray-800/50 rounded-lg space-y-2">
                          <div className="flex items-center gap-2 text-white">
                            <User className="w-4 h-4 text-gray-400" />
                            {selectedOrder.customer.name || 'Unbekannt'}
                          </div>
                          {selectedOrder.customer.phone && (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Phone className="w-4 h-4" />
                              {selectedOrder.customer.phone}
                            </div>
                          )}
                          {selectedOrder.customer.address && (
                            <div className="flex items-start gap-2 text-gray-400 text-sm">
                              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {selectedOrder.customer.address}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Items */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-400 mb-2">Artikel</h4>
                        <div className="space-y-2">
                          {selectedOrder.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-white">
                                {item.quantity}x {item.name}
                              </span>
                              <span className="text-gray-400">
                                {((item.price || 0) * (item.quantity || 1)).toFixed(2)} €
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Total */}
                      <div className="border-t border-gray-700 pt-3">
                        <div className="flex justify-between text-lg font-bold">
                          <span className="text-white">Gesamt</span>
                          <span className="text-emerald-400">
                            {(selectedOrder.total || 0).toFixed(2)} €
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-4">
                        {selectedOrder.status === 'new' && (
                          <Button
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                            onClick={() => updateOrderStatus(selectedOrder.order_id, 'processing')}
                          >
                            Annehmen
                          </Button>
                        )}
                        {selectedOrder.status === 'processing' && (
                          <Button
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => updateOrderStatus(selectedOrder.order_id, 'ready')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Fertig
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => updateOrderStatus(selectedOrder.order_id, 'cancelled')}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
